/**
 * useCustomersLayer — Sprint 4.2D.
 *
 * Renderiza customers IXC (com lat/lng) como marcadores casa 🏠 no mapa.
 * Click abre popup leve com nome / cidade / bairro / status / external_id.
 */
import { useEffect, useRef } from "react";

const STATUS_COLOR = {
  ativo:        "#22c55e",
  cancelado:    "#FF5063",
  bloqueado:    "#FF8A3C",
  suspenso:     "#FFB224",
  default:      "#94a3b8",
};

function statusColor(status) {
  if (!status) return STATUS_COLOR.default;
  const s = String(status).toLowerCase();
  return STATUS_COLOR[s] || STATUS_COLOR.default;
}

function customerHtml(c) {
  const color = statusColor(c.status);
  return `<div class="ipwd-customer-marker" style="--cust-color:${color}">
    <span>🏠</span>
  </div>`;
}

function popupHtml(c) {
  const status = c.status ? String(c.status) : "—";
  const statusColor_ = statusColor(c.status);
  return `<div class="ipwd-customer-popup">
    <div class="ipwd-customer-popup-header">
      <div class="ipwd-customer-popup-name">${escapeHtml(c.name)}</div>
      <div class="ipwd-customer-popup-code">${escapeHtml(c.code || "")}</div>
    </div>
    <div class="ipwd-customer-popup-grid">
      ${c.city ? `<div><b>Cidade:</b> ${escapeHtml(c.city)}</div>` : ""}
      ${c.neighborhood ? `<div><b>Bairro:</b> ${escapeHtml(c.neighborhood)}</div>` : ""}
      ${c.address ? `<div class="ipwd-customer-popup-addr">${escapeHtml(c.address)}</div>` : ""}
      <div><b>Status:</b> <span style="color:${statusColor_}">●</span> ${escapeHtml(status)}</div>
      ${c.phone ? `<div><b>Tel:</b> ${escapeHtml(c.phone)}</div>` : ""}
      ${c.external_id ? `<div class="ipwd-customer-popup-ext">IXC ID: <code>${escapeHtml(c.external_id)}</code></div>` : ""}
    </div>
  </div>`;
}

function escapeHtml(s) {
  return String(s ?? "").replace(/[&<>"']/g, (m) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[m])
  );
}

/**
 * @param {object} L
 * @param {object} mapRef
 * @param {Array}  customers (lista de CustomerExtendedOut, só os com coords)
 * @param {bool}   visible
 * @param {function} onCustomerClick(customer)
 */
export function useCustomersLayer({ L, mapRef, customers, visible, onCustomerClick }) {
  const groupRef = useRef(null);

  useEffect(() => {
    const map = mapRef?.current;
    if (!map || !L) return;
    if (!groupRef.current) groupRef.current = L.layerGroup().addTo(map);

    const group = groupRef.current;
    group.clearLayers();

    if (!visible || !customers || customers.length === 0) return;

    for (const c of customers) {
      if (c.lat == null || c.lng == null) continue;
      const icon = L.divIcon({
        className: "ipwd-customer-wrap",
        html: customerHtml(c),
        iconSize: [24, 24],
        iconAnchor: [12, 12],
      });
      const m = L.marker([c.lat, c.lng], { icon, zIndexOffset: 150 });
      m.bindTooltip(c.name, {
        direction: "top", offset: [0, -12], opacity: 1,
        className: "ipwd-marker-tooltip",
      });
      m.bindPopup(popupHtml(c), {
        maxWidth: 300, minWidth: 220, closeButton: true,
        className: "ipwd-popup",
      });
      if (onCustomerClick) {
        m.on("click", () => onCustomerClick(c));
      }
      m.addTo(group);
    }

    return undefined;
  }, [L, mapRef, customers, visible, onCustomerClick]);

  useEffect(() => {
    return () => {
      const map = mapRef?.current;
      if (map && groupRef.current) {
        map.removeLayer(groupRef.current);
        groupRef.current = null;
      }
    };
  }, [mapRef]);
}
